import { cx } from '@emotion/css';
import type { CSSObject } from '@emotion/react';
import type { Placement } from '@floating-ui/react';
import type { ComponentProps, ReactElement, ReactNode } from 'react';
import type { IconName } from '../Icon';
import Icon from '../Icon';
import ODSTooltip from '../ODSTooltip';

type Props = {
  /** What do you want to tell the user */
  message: ReactNode;
  /** What color should the icon be */
  color?:
    | 'primary'
    | 'danger'
    | 'black'
    | 'secondary'
    | 'white'
    | 'light'
    | 'info'
    | 'warning'
    | 'help'
    | 'synced'
    | 'inherit';
  className?: string;
  styles?: CSSObject;
  /** Where do you want the popover to open */
  placement?: Placement;
  /** Add some text next to the icon */
  label?: ReactNode;
  /** Add a title to the popover */
  title?: ReactNode;
  /** Is it a tooltip instead of a popover */
  tooltip?: boolean;
  /** Want an arrow on the tooltip? */
  tooltipArrow?: boolean;
  /** How far away should the popover be */
  offset?: number;
  /** Pass in the hover target */
  children?: ReactNode;
  /** Pass in the icon name */
  icon?: IconName;
  /** Allow the tooltip to be hovered over */
  hoverable?: boolean;
  /** testId selector for integration/e2e testing */
  testId?: string;
  /** Should the component be disabled */
  disabled?: boolean;
};

function ODSInfo({
  testId,
  message,
  color = 'primary',
  className,
  placement = 'right',
  icon,
  children,
  title,
  tooltip,
  tooltipArrow,
  offset = 8,
  styles,
  hoverable,
  disabled,
}: Props): ReactElement {
  let infoIcon = icon;
  let tooltipColor: ComponentProps<typeof ODSTooltip>['color'];

  if (!children && !icon) {
    switch (color) {
      case 'info':
        infoIcon = 'circle-info';
        tooltipColor = 'info';
        break;
      case 'danger':
        infoIcon = 'hexagon-exclamation';
        tooltipColor = 'danger';
        break;
      case 'warning':
        infoIcon = 'triangle-exclamation';
        tooltipColor = 'warning';
        break;
      case 'help':
        infoIcon = 'circle-question';
        break;
      default:
        infoIcon = 'circle-info';
        tooltipColor = 'info';
    }
  }

  return (
    <ODSTooltip
      arrow={tooltip && tooltipArrow}
      placement={placement}
      maxWidth="15rem"
      offset={offset}
      popover={!tooltip}
      title={title}
      message={message}
      ignoreOverflow
      hoverable={hoverable}
      color={tooltip ? tooltipColor : undefined}
    >
      <span
        data-testid={testId}
        className={cx(
          'd-inline-flex align-items-center gap-1 user-select-none small',
          color !== 'inherit' && `text-${color}`,
          className
        )}
        css={{
          cursor: 'help',
          textDecoration: 'underline dotted',
          '&:focus': { outline: 'none' },
          ...(disabled && { pointerEvents: 'none', opacity: 0.3 }),
          ...styles,
        }}
        tabIndex={0}
      >
        {!!infoIcon && <Icon name={infoIcon} />}
        {children}
      </span>
    </ODSTooltip>
  );
}

export default ODSInfo;
